import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Button, 
  Box, 
  TextField, 
  InputAdornment, 
  Divider,
  alpha
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SearchIcon from '@mui/icons-material/Search';
import LoginDropdown from './LoginDropdown';
import VideoSettings from './VideoSettings';
import UserMenu from './UserMenu';
import MiniVideoUploader from './MiniVideoUploader';
import axios from '../api/axiosConfig';
import { Account } from '../App';

interface HeaderProps {
  isAuthenticated: boolean;
  currentAccount: Account | null;
  allAccounts: Account[];
  onLogin: (token: string, userId: string, username: string) => void;
  onLogout: () => void;
  onLogoutAll: () => void;
  onSwitchAccount: (userId: string) => void;
  onSearch?: (results: any[]) => void;
  isPlayerView?: boolean;
  isMusicView?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  isAuthenticated, 
  currentAccount,
  allAccounts,
  onLogin,
  onLogout,
  onLogoutAll,
  onSwitchAccount,
  onSearch,
  isPlayerView = false,
  isMusicView = false
}) => {
  const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const navigate = useNavigate();

  const handleHomeClick = (event: React.MouseEvent) => {
    event.preventDefault();
    navigate('/', { replace: true });
  };

  const handleLoginClick = (event: React.MouseEvent<HTMLElement>) => {
    setLoginAnchorEl(event.currentTarget);
  };

  const handleLoginClose = () => {
    setLoginAnchorEl(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (!isAuthenticated || !searchQuery.trim()) {
      if (onSearch) {
        onSearch([]);
      }
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/videos/search?query=${encodeURIComponent(searchQuery)}`);
        if (onSearch) {
          onSearch(response.data.videos || []);
        }
      } catch (error) {
        console.error('Search error:', error);
        if (onSearch) {
          onSearch([]);
        }
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, isAuthenticated, onSearch, backendUrl]);

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        top: 0, 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: isMusicView ? 'rgba(0,0,0,0.4)' : '#000000',
        borderBottom: isMusicView ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255,255,255,0.1)',
        backdropFilter: isMusicView ? 'blur(20px) saturate(180%)' : 'blur(10px)',
        transition: 'background-color 0.5s ease, border-bottom 0.5s ease',
      }}
    >
      <Toolbar variant="dense" sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2 }}>
        {/* Logo Section - Responsive */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            component={RouterLink}
            to="/"
            edge="start"
            onClick={handleHomeClick}
            sx={{ 
              mr: 1,
              display: { xs: 'flex', sm: 'none' } 
            }}
          >
            <VideoLibraryIcon />
          </IconButton>

          <Typography 
            variant="h6" 
            component={RouterLink} 
            to="/" 
            onClick={handleHomeClick}
            sx={{ 
              textDecoration: 'none', 
              color: 'white',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              display: { xs: 'none', sm: 'block' },
              '&:hover': { opacity: 0.8 }
            }}
          >
            📺
          </Typography>
        </Box>
        
        {/* Search Bar - Centered & Sleek */}
        {isAuthenticated && (
          <Box sx={{ 
            flexGrow: 1, 
            mx: { xs: 2, sm: 4 }, 
            maxWidth: 600,
            display: 'flex',
            justifyContent: 'center'
          }}>
            <TextField
              size="small"
              placeholder="Search videos..."
              value={searchQuery}
              onChange={handleSearchChange}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#fff', 0.08),
                  borderRadius: 3,
                  color: 'white',
                  transition: 'all 0.2s',
                  '& fieldset': { border: 'none' },
                  '&:hover': { bgcolor: alpha('#fff', 0.12) },
                  '&.Mui-focused': { 
                    bgcolor: alpha('#fff', 0.15),
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.1)'
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1,
                  px: 2,
                  '&::placeholder': { color: alpha('#fff', 0.5) },
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: alpha('#fff', 0.5) }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Right Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAuthenticated && isPlayerView && (
            <Box sx={{ display: { xs: 'none', md: 'block' }, mr: 1 }}>
              <MiniVideoUploader size="small" />
            </Box>
          )}

          {isAuthenticated && currentAccount ? (
            <>
              <Box sx={{ display: { xs: isPlayerView ? 'none' : 'flex', sm: 'flex' } }}>
                <VideoSettings />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: isPlayerView ? 'none' : 'block', sm: 'block' }, bgcolor: alpha('#fff', 0.2), mx: 1, height: 24, alignSelf: 'center' }} />
              <UserMenu 
                currentAccount={currentAccount}
                allAccounts={allAccounts}
                onLogout={onLogout}
                onLogoutAll={onLogoutAll}
                onSwitchAccount={onSwitchAccount}
                onAddAccount={onLogin}
              />
            </>
          ) : (
            <>
              <Button 
                color="inherit" 
                onClick={handleLoginClick}
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                Login
              </Button>
              <LoginDropdown 
                anchorEl={loginAnchorEl}
                onClose={handleLoginClose}
                onLogin={onLogin}
              />
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
